// BOARD-GATES (docs/design/2026-11-02-board-stages-gates-kpis.md §2.2): a card in a column only a
// person moves cards out of shows one strip — "Review needs a person · Valentina decides" — with
// Approve · Rework · Reject for whoever decides it (disabled, with who does, for everyone else).
// Rework and Reject ask for a note (at least 10 characters), posted to the card and given to the
// DE when it is woken. From `GET tasks/{id}/review`; no gate (or column gates off), nothing shown.

import type { ColumnGateView } from "@companyos/schema/contracts";
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Label,
  Textarea,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, LockIcon, Undo2Icon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../../api/client";
import { problemText } from "../../settings/problem-text";
import { invalidateWork, workKeys } from "../work-queries";

type Decision = "approve" | "rework" | "reject";

/** A rework or reject note's floor (the server's `REVIEW_NOTE_MIN`). */
export const REVIEW_NOTE_MIN = 10;

/** Keyed by the card's status too: a card moved by drag reads its new column's gate. */
const reviewQuery = (api: Api, co: string, task: string, status: string) =>
  queryOptions({
    queryKey: [...workKeys.task(co, task), "review", status] as const,
    queryFn: () => api.client.tasks.reviewGate({ params: { co, id: task } }),
  });

export function ReviewStrip({
  api,
  co,
  taskId,
  status,
  variant = "strip",
  gate: given,
}: {
  /** The gate already known (the Inbox list's `review_gate`); skips the fetch. */
  gate?: ColumnGateView | undefined;
  api: Api;
  co: string;
  taskId: string;
  status: string;
  /** `row`: the inbox row's buttons only, and only for whoever may decide (same rules, same endpoint). */
  variant?: "strip" | "row";
}) {
  const qc = useQueryClient();
  // An Inbox row hands over the gate its list response carried: no fetch of its own.
  const q = useQuery({ ...reviewQuery(api, co, taskId, status), enabled: given === undefined });
  const [asking, setAsking] = useState<Exclude<Decision, "approve"> | null>(null);
  const [note, setNote] = useState("");
  // DECISION-LEDGER: a send-back or reject of wrong work counts as a reversal on the DE's record.
  const [wrong, setWrong] = useState(false);
  const id = useId();
  const decide = useMutation({
    mutationFn: (d: Decision) =>
      api.client.tasks.review({
        params: { co, id: taskId },
        body:
          d === "approve"
            ? { decision: d }
            : { decision: d, note: note.trim(), ...(wrong ? { wrong: true } : {}) },
      }),
    onSuccess: () => {
      setAsking(null);
      setNote("");
      setWrong(false);
      void qc.invalidateQueries({ queryKey: workKeys.task(co, taskId) });
      if (variant === "row") void invalidateWork(qc, co);
    },
  });
  const g = given ?? q.data?.gate;
  if (!g || (variant === "row" && !g.can_decide)) return null;
  const who = g.approver_name ?? "The card's owner";
  const disabledWhy = g.can_decide ? undefined : `${who} decides this column`;
  const short = note.trim().length < REVIEW_NOTE_MIN;
  const row = variant === "row";
  const Wrap = row ? "div" : "section";
  return (
    <Wrap
      {...(row
        ? { "data-slot": "review-row", className: "mt-1 flex flex-wrap items-center gap-2" }
        : {
            "aria-label": "Needs a person",
            "data-slot": "review-strip",
            className:
              "flex min-h-10 flex-wrap items-center gap-2 border-b border-subtle px-6 py-1.5 text-sm max-md:px-4",
          })}
    >
      {row ? null : (
        <>
          <LockIcon aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-muted-foreground">
            {g.column_name} needs a person · {who} decides
          </span>
        </>
      )}
      <Button
        size="xs"
        disabled={!g.can_decide || decide.isPending}
        title={disabledWhy ?? `Approve: to ${g.approve_to.name}`}
        onClick={() => decide.mutate("approve")}
      >
        <CheckIcon aria-hidden="true" />
        Approve
      </Button>
      <Button
        variant="outline"
        size="xs"
        disabled={!g.can_decide || decide.isPending}
        title={disabledWhy ?? `Rework: to ${g.rework_to.name}`}
        onClick={() => setAsking("rework")}
      >
        <Undo2Icon aria-hidden="true" />
        Rework
      </Button>
      <Button
        variant="outline"
        size="xs"
        disabled={!g.can_decide || decide.isPending}
        title={disabledWhy ?? `Reject: to ${g.reject_to.name}`}
        onClick={() => setAsking("reject")}
      >
        <XIcon aria-hidden="true" />
        Reject
      </Button>
      {decide.isError && !asking ? (
        <p role="alert" className="w-full text-xs text-destructive">
          {problemText(decide.error, "The decision could not be saved.")}
        </p>
      ) : null}
      <Dialog open={asking !== null} onOpenChange={(o) => !o && setAsking(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {asking === "reject"
                ? `Reject to ${g.reject_to.name}?`
                : `Send back to ${g.rework_to.name}?`}
            </DialogTitle>
            <DialogDescription>
              {asking === "reject"
                ? "Say why, so it is not filed again. The note is posted on the card."
                : "Say what to change. The note is posted on the card and the DE is woken with it."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Note</FieldLabel>
            <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-wrong`}
              checked={wrong}
              onCheckedChange={(on) => setWrong(on === true)}
            />
            <Label htmlFor={`${id}-wrong`} className="font-normal">
              The work was wrong (counts as a reversal on the agent's record)
            </Label>
          </div>
          {decide.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {problemText(decide.error, "The decision could not be saved.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
            <Button
              disabled={short || decide.isPending}
              onClick={() => asking && decide.mutate(asking)}
            >
              {asking === "reject" ? "Reject" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}
